import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

interface UserPreferences {
  theme?: string | null;
}

interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  role: string;
  emailVerified?: boolean;
  preferences?: UserPreferences;
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  /** @deprecated use isLoggedIn */
  isAuthenticated: boolean;
  loading: boolean;
  showAuthModal: boolean;
  showEmailVerification: boolean;
  authModalTab: 'login' | 'register';
  authModalMessage: string | null;
  openAuthModal: (tab?: 'login' | 'register', message?: string) => void;
  closeAuthModal: () => void;
  dismissEmailVerification: () => void;
  updateUser: (updates: Partial<User>) => void;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (name: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  requireAuth: (callback: () => void) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

function needsEmailVerification(user: User | null | undefined) {
  return Boolean(user && user.role !== 'admin' && user.emailVerified === false);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register'>('login');
  const [authModalMessage, setAuthModalMessage] = useState<string | null>(null);

  useEffect(() => {
    api.getMe()
      .then((res: any) => {
        if (res.success && res.data?.user) {
          const nextUser = res.data.user as User;
          setUser(nextUser);
          setShowEmailVerification(needsEmailVerification(nextUser));
        }
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : '';
        if (
          message.includes('Invalid token') ||
          message.includes('Access denied') ||
          message.includes('401')
        ) {
          api.removeToken();
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const openAuthModal = useCallback((tab: 'login' | 'register' = 'login', message?: string) => {
    setAuthModalTab(tab);
    setAuthModalMessage(message ?? null);
    setShowAuthModal(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
    setAuthModalMessage(null);
  }, []);

  const dismissEmailVerification = useCallback(() => {
    setShowEmailVerification(false);
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const handleAuthSuccess = useCallback((nextUser: User) => {
    setUser(nextUser);
    setShowAuthModal(false);
    setShowEmailVerification(needsEmailVerification(nextUser));

    const redirect = sessionStorage.getItem('auth_redirect');
    if (redirect && nextUser.role === 'admin') {
      sessionStorage.removeItem('auth_redirect');
      window.location.assign(redirect);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const result = await api.login(email, password);
      if (result.success && result.data?.user) {
        handleAuthSuccess(result.data.user);
        return { success: true };
      }
      return { success: false, error: result.error || 'Giriş başarısız' };
    } catch (err: any) {
      return { success: false, error: err.message || 'Bağlantı hatası' };
    }
  }, [handleAuthSuccess]);

  const register = useCallback(async (name: string, email: string, password: string) => {
    try {
      const result = await api.register(name, email, password);
      if (result.success && result.data?.user) {
        handleAuthSuccess(result.data.user);
        return { success: true };
      }
      return { success: false, error: result.error || 'Kayıt başarısız' };
    } catch (err: any) {
      return { success: false, error: err.message || 'Bağlantı hatası' };
    }
  }, [handleAuthSuccess]);

  const logout = useCallback(() => {
    api.logout();
    setUser(null);
    setShowEmailVerification(false);
  }, []);

  const requireAuth = useCallback((callback: () => void) => {
    if (user) {
      if (needsEmailVerification(user)) {
        setShowEmailVerification(true);
        return;
      }
      callback();
    } else {
      openAuthModal('login');
    }
  }, [user, openAuthModal]);

  return (
    <AuthContext.Provider value={{
      user, isLoggedIn: !!user, isAuthenticated: !!user, loading,
      showAuthModal, showEmailVerification, authModalTab, authModalMessage,
      openAuthModal, closeAuthModal, dismissEmailVerification,
      updateUser, login, register, logout, requireAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
